/**
 * /admin — Owner Console landing.
 *
 * Identity strip → action queues → KPIs → user segments → cross-app
 * shortcuts → recent marketplace promotions.
 *
 * Data sources: /api/admin/v2/dashboard/summary/ (KPIs + queues +
 * segments) and /marketplace/promotions/ (recent fan-out activity).
 * The two fetches are independent — one stalling never blocks the
 * other. Queue counts auto-refresh every 60 s so refunds / scorer
 * listings / open feedback stay honest without a manual click.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw,
  Package, Receipt, ShieldAlert,
  MessageSquare, Mic, GitMerge, AlertTriangle, ExternalLink, Plus,
} from "lucide-react";
import useAuthStore from "../../store/auth.store";
import {
  getDashboardSummary, listMarketplacePromotions,
} from "../../api/owner.api";
import { toArray } from "../../utils/toArray";

const QUEUE_POLL_MS = 60_000;

export default function AdminDashboard() {
  const { user } = useAuthStore();
  const [summary, setSummary] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [promotionsLoading, setPromotionsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [promotionsError, setPromotionsError] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await getDashboardSummary();
      setSummary(res.data);
      setSummaryError(null);
    } catch (e) {
      setSummaryError(e?.response?.data?.detail || e.message);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchPromotions = useCallback(async () => {
    try {
      const res = await listMarketplacePromotions({ page_size: 8 });
      setPromotions(toArray(res.data));
      setPromotionsError(null);
    } catch (e) {
      setPromotionsError(e?.response?.data?.detail || e.message);
    } finally {
      setPromotionsLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.allSettled([fetchSummary(), fetchPromotions()]);
    setRefreshing(false);
  }, [fetchSummary, fetchPromotions]);

  // Initial fetch + queue auto-refresh. Polling only re-pulls the summary
  // (where the queue counts live); promotions are low-churn so we leave
  // them alone until the user clicks Refresh.
  useEffect(() => {
    fetchSummary();
    fetchPromotions();
    const id = setInterval(fetchSummary, QUEUE_POLL_MS);
    return () => clearInterval(id);
  }, [fetchSummary, fetchPromotions]);

  const error = summaryError || promotionsError;

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ")
    || user?.username || "founder";

  return (
    <div className="min-h-screen bg-bg-main py-6 sm:py-8 px-3 sm:px-6">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Identity strip ────────────────────────────────────────── */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-text-secondary mb-1">
              Owner Console
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-text-primary">
              Welcome back, {displayName}
            </h1>
            <p className="text-text-secondary text-sm mt-1">
              Operational view of ZenCricketData.{" "}
              {summary?.as_of && (
                <span className="text-xs">As of {new Date(summary.as_of).toLocaleString()}.</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refreshAll}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 p-3 text-sm flex items-center justify-between gap-2"
          >
            <span className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span className="truncate">{error}</span>
            </span>
            <button
              onClick={refreshAll}
              disabled={refreshing}
              className="shrink-0 text-xs px-2 py-1 rounded border border-red-500/60 hover:bg-red-500/20 disabled:opacity-50"
            >
              Retry
            </button>
          </div>
        )}

        {/* Action items strip — what needs your eyeballs right now ── */}
        <ActionQueues s={summary} loading={summaryLoading} />

        {/* MTD financials + today's-glance ────────────────────────── */}
        <Section title="Month-to-date">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi
              label="Revenue (INR)"
              value={fmtInr(summary?.mtd?.revenue_inr)}
              hint={`${fmtInr(summary?.mtd?.topup_inr)} topups · ${fmtInr(summary?.mtd?.subscriptions_inr || 0)} subs · ${fmtInr(summary?.mtd?.pack_sales_inr)} packs`}
              color="emerald"
            />
            <Kpi
              label="AI cost (USD)"
              value={summary?.mtd?.ai_cost_usd != null ? `$${summary.mtd.ai_cost_usd}` : "—"}
              hint="Anthropic + Whisper"
              color="indigo"
            />
            <Kpi
              label="Active subs"
              value={summary?.subscriptions?.active ?? "—"}
              hint={`${summary?.subscriptions?.active_trials ?? "—"} trials live`}
              color="blue"
            />
            <Kpi
              label="Signups (7d)"
              value={summary?.week?.signups ?? "—"}
              hint={`${summary?.today?.signups ?? 0} today · ${summary?.today?.matches_created ?? 0} matches`}
              color="amber"
            />
          </div>
        </Section>

        {/* User segment breakdown — B2C / B2B / Staff ──────────────── */}
        <Section title="Users by segment" hint="Click a segment to open the filtered Users page.">
          {/* 2026-06-05 — was hardcoded grid-cols-3 which crushed the
              segment cards on phones (~80px wide each). Stack one
              per row on phones, two on small tablets, three on md+. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <SegmentCard
              to="/admin/users?segment=b2c"
              label="B2C"
              count={summary?.users?.b2c}
              total={summary?.users?.total}
              hint="Solo subscribers"
              color="emerald"
            />
            <SegmentCard
              to="/admin/users?segment=b2b"
              label="B2B"
              count={summary?.users?.b2b}
              total={summary?.users?.total}
              hint="Organization users"
              color="blue"
            />
            <SegmentCard
              to="/admin/users?segment=staff"
              label="Staff"
              count={summary?.users?.staff}
              total={summary?.users?.total}
              hint="Manivannan + sub-admins"
              color="rose"
            />
          </div>
        </Section>

        {/* Cross-app shortcuts — tools that live OUTSIDE the admin nav
            (cricket workspace surfaces + the Django emergency editor).
            Sidebar already covers every /admin/* route, so we don't
            duplicate those tiles here. */}
        <Section title="Cross-app shortcuts">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile
              icon={ShieldAlert} accent="rose" to="/cricket/disputed-balls"
              title="Disputed balls"
              desc="Scoring conflicts the data team has flagged for review."
            />
            <Tile
              icon={GitMerge} accent="slate" to="/cricket/dedupe"
              title="Dedupe"
              desc="Merge duplicate Player / Team / Stadium rows."
            />
            <Tile
              icon={Mic} accent="purple" to="/cricket/voice-analytics"
              title="Voice analytics"
              desc="STT accuracy, voice-corrections, alias proposals."
            />
            <Tile
              icon={ExternalLink} accent="rose" external
              to="https://api.zencricketdata.com/admin/"
              title="Django admin"
              desc="Emergency raw-table editor. Avoid day-to-day; use SPA surfaces."
            />
          </div>
        </Section>

        {/* Recent marketplace promotions — what blasts went out ─── */}
        <Section
          title="Recent marketplace publishes"
          hint="Latest fan-out events from services_promote — WhatsApp / email / in-app."
        >
          <RecentPromotions rows={promotions} loading={promotionsLoading} />
        </Section>

      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────

function Section({ title, hint, children }) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xs uppercase tracking-widest text-text-secondary">{title}</h2>
        {hint && <span className="text-xs text-text-secondary">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function Kpi({ label, value, hint, color = "emerald" }) {
  const colorMap = {
    emerald: "text-emerald-300",
    indigo:  "text-indigo-300",
    blue:    "text-blue-300",
    amber:   "text-amber-300",
    purple:  "text-purple-300",
    rose:    "text-rose-300",
  };
  return (
    <div className="rounded-lg border border-border-default bg-bg-primary p-4">
      <div className="text-[10px] uppercase tracking-widest text-text-secondary">{label}</div>
      <div className={`text-2xl font-semibold mt-1 font-mono ${colorMap[color]}`}>{value ?? "—"}</div>
      {hint && <div className="text-[11px] text-text-secondary mt-1">{hint}</div>}
    </div>
  );
}

function ActionQueues({ s, loading }) {
  // Highest-attention items: refunds awaiting approval + scorer
  // listings pending review. Rendered as bright cards on the top of
  // the page so the founder sees them on entry.
  const items = [
    {
      icon: Receipt,
      label: "Refunds awaiting approval",
      count: s?.queues?.refunds_pending ?? 0,
      // RefundRequest is only registered on the unified ZenAdminSite
      // (apps/payments/admin.py); no SPA surface exists, so the queue
      // card opens the emergency console in a new tab.
      //
      // NB the path is /admin/, not /zen-admin/. Those are different
      // surfaces: ZenAdminSite (the Django admin, mounted at /admin/)
      // vs the zen_admin HTMX app at /zen-admin/, which has no per-model
      // routes — /zen-admin/payments/refundrequest/ 404s. Must stay
      // absolute to the api host: zencricketdata.com/admin/* is the
      // React console.
      to: "https://api.zencricketdata.com/admin/payments/refundrequest/",
      external: true,
      color: "amber",
    },
    {
      icon: Package,
      label: "Scorer listings pending review",
      count: s?.queues?.scorer_listings_pending ?? 0,
      // ScorerMarketplace defaults its tab state to "pending", so no
      // querystring is needed — landing on /admin/scorer-marketplace
      // shows the pending queue directly.
      to: "/admin/scorer-marketplace",
      color: "indigo",
    },
    {
      icon: MessageSquare,
      label: "Open feedback threads",
      count: s?.queues?.open_feedback ?? 0,
      to: "/admin/feedback",
      color: "purple",
    },
  ];
  const COLOR_BG = {
    amber:  "bg-amber-500/10 border-amber-500/40 hover:bg-amber-500/15",
    indigo: "bg-indigo-500/10 border-indigo-500/40 hover:bg-indigo-500/15",
    purple: "bg-purple-500/10 border-purple-500/40 hover:bg-purple-500/15",
  };
  const COLOR_TEXT = {
    amber:  "text-amber-300",
    indigo: "text-indigo-300",
    purple: "text-purple-300",
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {items.map((q) => {
        const Icon = q.icon;
        const dim = !loading && q.count === 0;
        const statusText = loading ? "Loading" : dim ? "All clear" : "Needs attention";
        const ariaLabel = `${q.label}: ${loading ? "loading" : q.count} — ${statusText.toLowerCase()}${q.external ? " (opens in new tab)" : ""}`;
        const cls = `rounded-lg border p-4 transition flex items-center justify-between gap-3 ${
          dim ? "border-border-default bg-bg-primary opacity-60" : COLOR_BG[q.color]
        }`;
        const inner = (
          <>
            <span className="flex items-center gap-3 min-w-0">
              <Icon
                aria-hidden="true"
                className={`w-5 h-5 shrink-0 ${dim ? "text-text-secondary" : COLOR_TEXT[q.color]}`}
              />
              <span className="min-w-0">
                <span className="text-sm block truncate">{q.label}</span>
                <span className={`text-[10px] uppercase tracking-widest ${
                  dim ? "text-text-secondary" : COLOR_TEXT[q.color]
                }`}>
                  {statusText}
                </span>
              </span>
            </span>
            <span
              aria-hidden="true"
              className={`font-mono text-2xl font-bold tabular-nums shrink-0 ${
                dim ? "text-text-secondary" : COLOR_TEXT[q.color]
              }`}
            >
              {loading ? "…" : q.count}
            </span>
          </>
        );
        if (q.external) {
          return (
            <a
              key={q.label}
              href={q.to}
              target="_blank"
              rel="noreferrer"
              aria-label={ariaLabel}
              className={cls}
            >
              {inner}
            </a>
          );
        }
        return (
          <Link
            key={q.label}
            to={q.to}
            aria-label={ariaLabel}
            className={cls}
          >
            {inner}
          </Link>
        );
      })}
    </div>
  );
}

function SegmentCard({ to, label, count, total, hint, color = "emerald" }) {
  const COLOR_BG = {
    emerald: "border-emerald-500/30 hover:border-emerald-500/60 hover:bg-emerald-500/5",
    blue:    "border-blue-500/30    hover:border-blue-500/60    hover:bg-blue-500/5",
    rose:    "border-rose-500/30    hover:border-rose-500/60    hover:bg-rose-500/5",
  };
  const COLOR_TXT = {
    emerald: "text-emerald-300",
    blue:    "text-blue-300",
    rose:    "text-rose-300",
  };
  const pct = total && count != null ? Math.round((count / total) * 100) : null;
  return (
    <Link
      to={to}
      className={`block rounded-lg border ${COLOR_BG[color]} bg-bg-primary p-4 transition-colors`}
    >
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-widest text-text-secondary">{label}</div>
        {pct != null && (
          <div className="text-[10px] text-text-secondary tabular-nums">{pct}%</div>
        )}
      </div>
      <div className={`text-2xl font-semibold mt-1 font-mono ${COLOR_TXT[color]}`}>
        {count?.toLocaleString("en-IN") ?? "—"}
      </div>
      <div className="text-[11px] text-text-secondary mt-1">{hint}</div>
    </Link>
  );
}

function Tile({ icon: Icon, accent, to, title, desc, badge, external }) {
  const BORDER = {
    emerald: "hover:border-emerald-500/60",
    indigo:  "hover:border-indigo-500/60",
    blue:    "hover:border-blue-500/60",
    amber:   "hover:border-amber-500/60",
    purple:  "hover:border-purple-500/60",
    rose:    "hover:border-rose-500/60",
    slate:   "hover:border-slate-500/60",
  };
  const ICON = {
    emerald: "text-emerald-400",
    indigo:  "text-indigo-400",
    blue:    "text-blue-400",
    amber:   "text-amber-400",
    purple:  "text-purple-400",
    rose:    "text-rose-400",
    slate:   "text-slate-400",
  };
  const cls = `block rounded-xl border border-bg-secondary bg-bg-primary p-4 transition group ${BORDER[accent]}`;
  const inner = (
    <>
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${ICON[accent]}`} />
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        {badge && (
          <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40">
            {badge}
          </span>
        )}
      </div>
      <p className="text-xs text-text-secondary leading-snug">{desc}</p>
    </>
  );
  return external
    ? <a href={to} target="_blank" rel="noreferrer" className={cls}>{inner}</a>
    : <Link to={to} className={cls}>{inner}</Link>;
}

function RecentPromotions({ rows, loading }) {
  if (loading && rows.length === 0) {
    return <Skel rows={3} />;
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-lg border border-border-default bg-bg-primary p-6 text-center text-sm">
        <p className="text-text-secondary mb-3">
          Nothing published yet. Publish a pack to start the activity feed.
        </p>
        <Link
          to="/admin/marketplace"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
        >
          <Plus className="w-4 h-4" />
          Open marketplace
        </Link>
      </div>
    );
  }
  const STATUS_COLOR = {
    sent:    "text-emerald-300",
    partial: "text-amber-300",
    skipped: "text-text-secondary",
    failed:  "text-red-300",
    queued:  "text-blue-300",
  };
  return (
    <div className="rounded-lg border border-border-default bg-bg-primary overflow-x-auto">
      <table className="w-full text-sm">
        <caption className="sr-only">Recent marketplace promotion fan-outs</caption>
        <thead className="text-[11px] uppercase tracking-widest text-text-secondary border-b border-border-default">
          <tr>
            <th scope="col" className="text-left px-3 py-2">When</th>
            <th scope="col" className="text-left px-3 py-2">Pack</th>
            <th scope="col" className="text-left px-3 py-2">Channel</th>
            <th scope="col" className="text-left px-3 py-2">Status</th>
            <th scope="col" className="text-right px-3 py-2">Recipients</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border-default first:border-t-0">
              <td className="px-3 py-1.5 text-xs text-text-secondary whitespace-nowrap">
                {new Date(r.sent_at).toLocaleString()}
              </td>
              <td className="px-3 py-1.5 truncate">{r.pack_name || "—"}</td>
              <td className="px-3 py-1.5 font-mono text-xs">{r.channel}</td>
              <td className={`px-3 py-1.5 ${STATUS_COLOR[r.status] || ""}`}>
                <span className="sr-only">Status: </span>{r.status}
              </td>
              <td className="px-3 py-1.5 text-right font-mono tabular-nums">{r.recipient_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Skel({ rows = 3 }) {
  return (
    <div className="rounded-lg border border-border-default bg-bg-primary p-3 space-y-2">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="h-6 rounded bg-bg-secondary animate-pulse" />
      ))}
    </div>
  );
}

function fmtInr(n) {
  if (n == null) return "—";
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}k`;
  return `₹${n}`;
}
